import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  useCompany,
  useDeleteCompany,
  useUpdateCompany,
  useLinkLexwareCompany,
  useUnlinkLexwareCompany,
  useSyncFromLexware,
  usePushToLexware,
  useEnrichCompany,
} from '../hooks';
import { useCrmSettings } from '../settings/CrmSettingsContext';
import { CompanyFormModal } from './CompanyFormModal';
import { ContactFormModal } from '../contacts/ContactFormModal';
import { ActivityFeed } from './ActivityFeed';
import { CompanyRelationshipsCard } from './CompanyRelationshipsCard';
import { ContractsCard } from './ContractsCard';
import { ProjectRequestsTab } from './ProjectRequestsTab';
import { LexwareSearchModal } from '../lexware/LexwareSearchModal';

import { ComplianceProfileTab } from './ComplianceProfileTab';
import { CpcTab } from './CpcTab';
import { useComplianceProfile } from '../hooks';
// ClockodoSearchModal entfernt — Auto-Match via Kundennummer
import { CustomFieldsDisplay } from '../CustomFieldsDisplay';
import { Modal } from '../../components/Modal';
import type { DealStatus, EnrichmentSuggestion } from '../types';
import styles from './CompanyDetailPage.module.css';

// ============================================================
//  Types
// ============================================================

type DetailTab = 'overview' | 'compliance' | 'sales' | 'history' | 'integrations';

// ============================================================
//  Constants & Helpers
// ============================================================

const TAB_LABELS: Record<DetailTab, string> = {
  overview: 'Übersicht',
  compliance: 'Compliance & CPC',
  sales: 'Vertrieb & Projekte',
  history: 'Historie & Kontakte',
  integrations: 'Integrationen',
};

const STATUS_COLORS: Record<DealStatus, { bg: string; color: string }> = {
  OPEN: { bg: '#dbeafe', color: '#1e40af' },
  WON: { bg: '#d1fae5', color: '#065f46' },
  LOST: { bg: '#fee2e2', color: '#991b1b' },
};

const STATUS_LABELS: Record<DealStatus, string> = {
  OPEN: 'Offen',
  WON: 'Gewonnen',
  LOST: 'Verloren',
};


function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// ============================================================
//  Sub-Component: Compliance & CPC Tab
// ============================================================

function ComplianceAndCpcTab({ companyId, clockodoCustomerId }: { companyId: string; clockodoCustomerId?: number | null }) {
  const [subTab, setSubTab] = useState<'profile' | 'cpc'>('profile');
  const { data: profileResponse, isLoading: profileLoading } = useComplianceProfile(companyId);
  const hasProfile = !profileLoading && !!profileResponse?.data;

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          onClick={() => setSubTab('profile')}
          style={{
            padding: '0.375rem 0.875rem',
            fontSize: '0.875rem',
            fontWeight: subTab === 'profile' ? 600 : 400,
            background: subTab === 'profile' ? 'var(--color-primary)' : 'transparent',
            color: subTab === 'profile' ? 'white' : 'var(--color-text-muted)',
            border: `1px solid ${subTab === 'profile' ? 'var(--color-primary)' : 'var(--color-border)'}`,
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >
          Compliance-Profil
        </button>
        <span title={!hasProfile ? 'Bitte zuerst ein Compliance-Profil anlegen' : undefined}>
          <button
            onClick={() => hasProfile && setSubTab('cpc')}
            disabled={!hasProfile}
            style={{
              padding: '0.375rem 0.875rem',
              fontSize: '0.875rem',
              fontWeight: subTab === 'cpc' ? 600 : 400,
              background: subTab === 'cpc' ? 'var(--color-primary)' : 'transparent',
              color: !hasProfile ? 'var(--color-text-muted)' : subTab === 'cpc' ? 'white' : 'var(--color-text-muted)',
              border: `1px solid ${subTab === 'cpc' ? 'var(--color-primary)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-sm)',
              cursor: !hasProfile ? 'not-allowed' : 'pointer',
              opacity: !hasProfile ? 0.45 : 1,
              transition: 'all 0.15s',
            }}
          >
            CPC-Kalkulator
          </button>
        </span>
      </div>
      {subTab === 'profile' && <ComplianceProfileTab companyId={companyId} />}
      {subTab === 'cpc' && hasProfile && <CpcTab companyId={companyId} clockodoCustomerId={clockodoCustomerId} />}
    </div>
  );
}

// ============================================================
//  Page Component
// ============================================================

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isModuleEnabled } = useCrmSettings();
  const lexwareEnabled = isModuleEnabled('lexware');
  const clockodoEnabled = isModuleEnabled('clockodo');

  // ---- Core data ----
  const { data, isLoading, error } = useCompany(id!);
  const deleteMutation = useDeleteCompany();

  // ---- UI State ----
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [isEditOpen, setEditOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [isCreateContactOpen, setCreateContactOpen] = useState(false);
  const [isLexwareSearchOpen, setLexwareSearchOpen] = useState(false);
  // isClockodoSearchOpen entfernt — Auto-Match via Kundennummer
  const [isEnrichOpen, setEnrichOpen] = useState(false);
  const [enrichSuggestions, setEnrichSuggestions] = useState<EnrichmentSuggestion[]>([]);

  // ---- Lexware Mutations (must be called before conditional returns) ----
  const linkCompany = useLinkLexwareCompany();
  const unlinkCompany = useUnlinkLexwareCompany();
  const syncFromLexware = useSyncFromLexware();
  const pushToLexware = usePushToLexware();
  const enrichCompany = useEnrichCompany();
  const updateCompany = useUpdateCompany();

  // ---- Lexware link state ----
  const companyData = data?.data;
  const isLinked = !!companyData?.lexwareContactId;

  // ---- Loading / Error ----
  if (isLoading) return <p>Laden...</p>;
  if (error || !data)
    return (
      <p style={{ color: 'var(--color-error)' }}>
        Unternehmen konnte nicht geladen werden
      </p>
    );

  const company = data.data;
  const contacts = company.contacts ?? [];
  const deals = company.deals ?? [];

  // Industry badge
  const industryColor = company.industryRef?.color ?? '#6366f1';
  const industryName = company.industryRef?.name ?? company.industry;

  // Lexware mutation state
  const isAnyLexwareMutating =
    linkCompany.isPending ||
    unlinkCompany.isPending ||
    syncFromLexware.isPending ||
    pushToLexware.isPending;

  return (
    <div>
      {/* Breadcrumb */}
      <nav style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
        <Link to="/crm?tab=companies" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>CRM</Link>
        <span style={{ fontSize: '0.6875rem' }}>›</span>
        <Link to="/crm?tab=companies" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>Unternehmen</Link>
        <span style={{ fontSize: '0.6875rem' }}>›</span>
        <span style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>{company.name}</span>
      </nav>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {/* Logo */}
          <div
            style={{
              width: company.logoUrl ? 'auto' : 72,
              minWidth: 72, maxWidth: 160,
              height: 72, borderRadius: 'var(--radius-md)',
              border: '1px solid rgba(255,255,255,0.3)',
              background: company.logoUrl ? 'white' : 'rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
              padding: company.logoUrl ? '0.375rem' : 0,
            }}
            title="Logo hochladen (Klick zum Ändern)"
            onClick={() => document.getElementById('company-logo-input')?.click()}
          >
            {company.logoUrl ? (
              <img src={company.logoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            )}
            <input
              id="company-logo-input"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || file.size > 2 * 1024 * 1024) return;
                const reader = new FileReader();
                reader.onload = () => {
                  const dataUrl = reader.result as string;
                  updateCompany.mutate({ id: company.id, data: { logoUrl: dataUrl } });
                };
                reader.readAsDataURL(file);
                e.target.value = '';
              }}
            />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h1 className={styles.name}>{company.name}</h1>
              {industryName && (
                <span
                  className={styles.industryBadge}
                  style={{ backgroundColor: industryColor }}
                >
                  {industryName}
                </span>
              )}
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: company.isActive
                    ? 'var(--color-success)'
                    : 'var(--color-error)',
                }}
                title={company.isActive ? 'Aktiv' : 'Inaktiv'}
              />
            </div>
            {(company.customerNumber || company.ownerName) && (
              <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.25rem', fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)' }}>
                {company.customerNumber && (
                  <span>Kd.-Nr.: <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{company.customerNumber}</strong></span>
                )}
                {company.ownerName && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                    Zuständig: <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{company.ownerName}</strong>
                    {company.businessEntity && (
                      <span style={{
                        fontSize: '0.6875rem', fontWeight: 700,
                        padding: '0.125rem 0.5rem', borderRadius: '999px',
                        color: 'white', background: '#6366f1',
                      }}>
                        {company.businessEntity.shortName ?? company.businessEntity.name}
                      </span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Status Badges */}
          {lexwareEnabled && (
            <span
              className={`${styles.lexwareBadge} ${
                isLinked ? styles.lexwareBadgeLinked : styles.lexwareBadgeNotLinked
              }`}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: isLinked ? '#059669' : '#dc2626',
                  display: 'inline-block',
                }}
              />
              Lexware Office
            </span>
          )}
          {clockodoEnabled && (
            <span
              className={`${styles.lexwareBadge} ${
                (company.clockodoCustomerId || company.customerNumber) ? styles.lexwareBadgeLinked : styles.lexwareBadgeNotLinked
              }`}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: (company.clockodoCustomerId || company.customerNumber) ? '#059669' : '#dc2626',
                  display: 'inline-block',
                }}
              />
              Clockodo
            </span>
          )}
          <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
          <button
            onClick={() => setEditOpen(true)}
            style={{
              padding: '0.375rem 0.875rem',
              fontSize: '0.8125rem',
              fontWeight: 600,
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            Bearbeiten
          </button>
          <button
            onClick={() => setDeleteOpen(true)}
            style={{
              padding: '0.375rem 0.625rem',
              fontSize: '0.8125rem',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.7)',
              marginLeft: '0.25rem',
            }}
            title="Unternehmen löschen"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z" />
            </svg>
          </button>
        </div>
      </div>

      {/* ======== Tab Bar ======== */}
      <div className={styles.tabBar}>
        {(Object.keys(TAB_LABELS) as DetailTab[]).map((tab) => (
          <button
            key={tab}
            className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* ======== Tab Content ======== */}
      <div className={styles.tabContent}>
        {/* ---- Tab: Übersicht ---- */}
        {activeTab === 'overview' && (
          <div className={styles.overviewGrid}>
            {/* Left: Stammdaten */}
            <div>
              <div className={styles.card}>
                <h2 className={styles.cardTitle}>Unternehmensdaten</h2>

                {/* Kundennummer + Zuständigkeit */}
                {(company.customerNumber || company.ownerName) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    {company.customerNumber && (
                      <div>
                        <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>Kundennummer</span>
                        <span className={styles.infoValue} style={{ fontWeight: 600 }}>{company.customerNumber}</span>
                      </div>
                    )}
                    {company.ownerName && (
                      <div>
                        <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>Zuständig</span>
                        <span className={styles.infoValue} style={{ fontWeight: 500 }}>{company.ownerName}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Zwei-Spalten: Links Adresse | Rechts Klassifizierung */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  {/* Linke Spalte: Adresse */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {(company.street || company.zip || company.city) && (
                      <div>
                        <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>Adresse</span>
                        <span className={styles.infoValue}>
                          {company.street && <>{company.street}<br /></>}
                          {company.zip} {company.city}
                          {company.country && company.country !== 'DE' && (
                            <>, {company.country}</>
                          )}
                        </span>
                      </div>
                    )}
                    {(company.deliveryStreet || company.deliveryZip || company.deliveryCity) && (
                      <div>
                        <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>Lieferadresse</span>
                        <span className={styles.infoValue}>
                          {company.deliveryStreet && <>{company.deliveryStreet}<br /></>}
                          {company.deliveryZip} {company.deliveryCity}
                          {company.deliveryCountry && company.deliveryCountry !== 'DE' && (
                            <>, {company.deliveryCountry}</>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Rechte Spalte: Kontotyp, Branche */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {company.accountType && (
                      <div>
                        <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>Kontotyp</span>
                        <span className={styles.infoValue}>{company.accountType.name}</span>
                      </div>
                    )}
                    {industryName && (
                      <div>
                        <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>Branche</span>
                        <span className={styles.infoValue}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: industryColor, display: 'inline-block' }} />
                            {industryName}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Trennlinie */}
                <div style={{ borderTop: '1px solid var(--color-border)', margin: '0.25rem 0 0.75rem' }} />

                {/* E-Mail + Telefon */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  {company.email && (
                    <div>
                      <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>E-Mail</span>
                      <span className={styles.infoValue}>
                        <a href={`mailto:${company.email}`} style={{ color: 'var(--color-primary)' }}>
                          {company.email}
                        </a>
                      </span>
                    </div>
                  )}
                  {company.phone && (
                    <div>
                      <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>Telefon</span>
                      <span className={styles.infoValue}>{company.phone}</span>
                    </div>
                  )}
                </div>

                {/* Website */}
                {company.website && (
                  <div style={{ marginBottom: '1rem' }}>
                    <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>Website</span>
                    <span className={styles.infoValue}>
                      <a href={/^https?:\/\//i.test(company.website) ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                        {company.website}
                      </a>
                    </span>
                  </div>
                )}

                {/* Trennlinie */}
                {(company.tradeRegisterNumber || company.vatId || company.taxId || company.companySize) && (
                  <div style={{ borderTop: '1px solid var(--color-border)', margin: '0.25rem 0 0.75rem' }} />
                )}

                {/* HRB + UstID */}
                {(company.tradeRegisterNumber || company.vatId) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    {company.tradeRegisterNumber && (
                      <div>
                        <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>Handelsregister</span>
                        <span className={styles.infoValue}>
                          {company.tradeRegisterNumber}
                          {company.registerCourt && (
                            <span style={{ color: 'var(--color-text-muted)' }}> ({company.registerCourt})</span>
                          )}
                        </span>
                      </div>
                    )}
                    {company.vatId && (
                      <div>
                        <span className={styles.infoLabel} style={{ display: 'block', marginBottom: '0.125rem' }}>USt-IdNr.</span>
                        <span className={styles.infoValue}>{company.vatId}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Steuernummer + Groesse */}
                <div className={styles.infoGrid}>
                  {company.taxId && (
                    <>
                      <span className={styles.infoLabel}>Steuernummer</span>
                      <span className={styles.infoValue}>{company.taxId}</span>
                    </>
                  )}
                  {company.companySize && (
                    <>
                      <span className={styles.infoLabel}>Groesse</span>
                      <span className={styles.infoValue}>
                        {{'SIZE_1_10': '1–10', 'SIZE_11_50': '11–50', 'SIZE_51_200': '51–200', 'SIZE_201_500': '201–500', 'SIZE_500_PLUS': '500+'}[company.companySize] ?? company.companySize} Mitarbeiter
                      </span>
                    </>
                  )}
                  {company.dataEnrichedAt && (
                    <>
                      <span className={styles.infoLabel}>Datenanreicherung</span>
                      <span className={styles.infoValue} style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                        {new Date(company.dataEnrichedAt).toLocaleDateString('de-DE')}
                        {company.dataEnrichedSource && ` (${company.dataEnrichedSource})`}
                      </span>
                    </>
                  )}
                </div>

                {/* Erstellt / Geändert */}
                <div style={{
                  display: 'flex', gap: '1.5rem', marginTop: '1rem', paddingTop: '0.75rem',
                  borderTop: '1px solid var(--color-border)',
                  fontSize: '0.75rem', color: 'var(--color-text-muted)',
                }}>
                  <span>Erstellt: {formatDate(company.createdAt)}</span>
                  {company.updatedAt && company.updatedAt !== company.createdAt && (
                    <span>Zuletzt geändert: {formatDate(company.updatedAt)}</span>
                  )}
                </div>

                {/* Notizen */}
                {company.notes && (
                  <div className={styles.notesSection}>
                    <span
                      className={styles.infoLabel}
                      style={{ display: 'block', marginBottom: '0.375rem' }}
                    >
                      Notizen
                    </span>
                    <p className={styles.notesText}>{company.notes}</p>
                  </div>
                )}

                {/* Tags */}
                {company.tags && company.tags.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <span
                      className={styles.infoLabel}
                      style={{ display: 'block', marginBottom: '0.375rem' }}
                    >
                      Tags
                    </span>
                    <div className={styles.tags}>
                      {company.tags.map((tag) => (
                        <span key={tag} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Fields */}
                {company.customFields && company.customFields.length > 0 && (
                  <CustomFieldsDisplay fields={company.customFields} />
                )}

              </div>
            </div>

            {/* Col 2: Kontakte */}
            <div className={styles.card}>
              <div className={styles.cardTitleRow}>
                <h2 className={styles.cardTitle} style={{ marginBottom: 0 }}>
                  Kontakte ({company._count?.contacts ?? contacts.length})
                </h2>
                <button
                  onClick={() => setCreateContactOpen(true)}
                  style={{
                    padding: '0.25rem 0.625rem', fontSize: '0.8125rem',
                    background: 'var(--color-primary)', color: 'white',
                    border: 'none', borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
                  }}
                >
                  + Kontakt
                </button>
              </div>
              {contacts.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8125rem', marginTop: '0.5rem' }}>
                  Keine Kontakte vorhanden
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.5rem' }}>
                  {contacts.map((c) => {
                    const cName = [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
                    const initials = [c.firstName?.[0], c.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?';
                    const avatarColors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#14b8a6'];
                    const avatarColor = avatarColors[(cName.charCodeAt(0) + (cName.charCodeAt(1) || 0)) % avatarColors.length];
                    return (
                      <div
                        key={c.id}
                        onClick={() => navigate(`/crm/contacts/${c.id}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.5rem 0.375rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                        onMouseOver={(e) => (e.currentTarget.style.background = 'var(--color-bg)')}
                        onMouseOut={(e) => (e.currentTarget.style.background = '')}
                      >
                        <span style={{ width: 30, height: 30, borderRadius: '50%', background: avatarColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6875rem', fontWeight: 700, flexShrink: 0 }}>
                          {initials}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cName}</div>
                          {c.position && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.position}</div>
                          )}
                        </div>
                        <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '9999px', padding: '0.125rem 0.625rem', fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          Profil
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Col 3: Compliance teaser + Deals teaser + Beziehungen */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div
                className={styles.card}
                style={{ cursor: 'pointer' }}
                onClick={() => setActiveTab('compliance')}
              >
                <div className={styles.cardTitleRow}>
                  <h2 className={styles.cardTitle} style={{ marginBottom: 0 }}>Compliance & CPC</h2>
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 500 }}>→ Öffnen</span>
                </div>
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                  Compliance-Profil und CPC-Kalkulator für dieses Unternehmen.
                </p>
              </div>
              {deals.length > 0 && (
                <div
                  className={styles.card}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setActiveTab('sales')}
                >
                  <div className={styles.cardTitleRow}>
                    <h2 className={styles.cardTitle} style={{ marginBottom: 0 }}>Vertrieb</h2>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 500 }}>→ Alle</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginTop: '0.5rem' }}>
                    {deals.slice(0, 3).map((deal) => (
                      <div key={deal.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8125rem' }}>
                        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '0.5rem' }}>{deal.title}</span>
                        <span style={{ ...STATUS_COLORS[deal.status as DealStatus], padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', flexShrink: 0 }}>
                          {STATUS_LABELS[deal.status as DealStatus]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <CompanyRelationshipsCard companyId={company.id} />
            </div>
          </div>
        )}

        {/* ---- Tab: Compliance & CPC ---- */}
        {activeTab === 'compliance' && (
          <ComplianceAndCpcTab companyId={company.id} clockodoCustomerId={company.clockodoCustomerId} />
        )}

        {/* ---- Tab: Vertrieb & Projekte ---- */}
        {activeTab === 'sales' && (
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '1.5rem', alignItems: 'start' }}>
            {/* Left column: Projekte (mit ERP Belege + Clockodo inline) */}
            <ProjectRequestsTab companyId={company.id} />
            {/* Right column: Verträge */}
            <ContractsCard companyId={company.id} />
          </div>
        )}

        {/* ---- Tab: Historie & Kontakte ---- */}
        {activeTab === 'history' && (
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.5rem', alignItems: 'start' }}>
            <div className={styles.card}>
              <div className={styles.cardTitleRow}>
                <h2 className={styles.cardTitle} style={{ marginBottom: 0 }}>
                  Kontakte ({contacts.length})
                </h2>
                <button
                  onClick={() => setCreateContactOpen(true)}
                  style={{
                    padding: '0.25rem 0.625rem', fontSize: '0.8125rem',
                    background: 'var(--color-primary)', color: 'white',
                    border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 600,
                  }}
                >
                  + Kontakt
                </button>
              </div>
              {contacts.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8125rem', marginTop: '0.5rem' }}>
                  Keine Kontakte vorhanden
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.5rem' }}>
                  {contacts.map((c) => {
                    const cName = [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
                    const initials = [c.firstName?.[0], c.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?';
                    const avatarColors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#14b8a6'];
                    const avatarColor = avatarColors[(cName.charCodeAt(0) + (cName.charCodeAt(1) || 0)) % avatarColors.length];
                    return (
                      <div
                        key={c.id}
                        onClick={() => navigate(`/crm/contacts/${c.id}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                        onMouseOver={(e) => (e.currentTarget.style.background = 'var(--color-bg)')}
                        onMouseOut={(e) => (e.currentTarget.style.background = '')}
                      >
                        <span style={{ width: 28, height: 28, borderRadius: '50%', background: avatarColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem', fontWeight: 700, flexShrink: 0 }}>
                          {initials}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cName}</div>
                          {c.position && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.position}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <ActivityFeed companyId={company.id} />
            </div>
          </div>
        )}

        {/* ---- Tab: Externe Verbindungen ---- */}
        {activeTab === 'integrations' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Lexware Office */}
            {lexwareEnabled && (
              <div className={styles.card}>
                <div className={styles.cardTitleRow}>
                  <h2 className={styles.cardTitle} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 0 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 22, height: 22, borderRadius: 'var(--radius-sm)',
                      background: '#dbeafe', color: '#2563eb',
                    }}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width={14} height={14}>
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                    </span>
                    Lexware Office
                  </h2>
                  <div style={{ display: 'flex', gap: '0.375rem' }}>
                    {!isLinked ? (
                      <button
                        className={styles.lexwareActionBtnPrimary}
                        onClick={() => setLexwareSearchOpen(true)}
                        disabled={isAnyLexwareMutating}
                      >
                        Verknüpfen
                      </button>
                    ) : (
                      <>
                        <button
                          className={styles.lexwareActionBtn}
                          onClick={() => syncFromLexware.mutate({ entityType: 'company', entityId: company.id })}
                          disabled={isAnyLexwareMutating}
                        >
                          {syncFromLexware.isPending ? 'Sync...' : 'Sync'}
                        </button>
                        <button
                          className={styles.lexwareActionBtn}
                          onClick={() => pushToLexware.mutate({ entityType: 'company', entityId: company.id })}
                          disabled={isAnyLexwareMutating}
                        >
                          {pushToLexware.isPending ? 'Push...' : 'Push'}
                        </button>
                        <button
                          className={styles.lexwareActionBtnDanger}
                          onClick={() => unlinkCompany.mutate(company.id)}
                          disabled={isAnyLexwareMutating}
                        >
                          {unlinkCompany.isPending ? 'Trennen...' : 'Trennen'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0' }}>
                  Synchronisation mit Lexware Office für Buchhaltung und Rechnungswesen.
                </p>
                {isLinked ? (
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                    <span style={{ color: '#059669', fontWeight: 500 }}>Verknüpft</span>
                    {' '}(Kontakt-ID: {company.lexwareContactId})
                    {company.lexwareSyncedAt && (
                      <span> · Letzte Sync: {formatDate(company.lexwareSyncedAt)}</span>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8125rem', color: '#dc2626', marginTop: '0.5rem', fontWeight: 500 }}>
                    Nicht verknüpft
                  </div>
                )}
              </div>
            )}

            {/* Clockodo */}
            {clockodoEnabled && (
              <div className={styles.card}>
                <div className={styles.cardTitleRow}>
                  <h2 className={styles.cardTitle} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 0 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 22, height: 22, borderRadius: 'var(--radius-sm)',
                      background: '#fef3c7', color: '#d97706',
                    }}>
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" width={14} height={14}>
                        <circle cx="10" cy="10" r="8" />
                        <path d="M10 5v5l3.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    Clockodo
                  </h2>
                </div>
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0' }}>
                  Zeiterfassung und Projektstunden aus Clockodo. Automatische Zuordnung via Kundennummer.
                </p>
                {company.clockodoCustomerId ? (
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                    <span style={{ color: '#059669', fontWeight: 500 }}>Verknüpft</span>
                    {' '}via Kundennummer {company.customerNumber ?? '—'}
                    {company.clockodoSyncedAt && (
                      <span> · {formatDate(company.clockodoSyncedAt)}</span>
                    )}
                  </div>
                ) : company.customerNumber ? (
                  <div style={{ fontSize: '0.8125rem', color: '#d97706', marginTop: '0.5rem', fontWeight: 500 }}>
                    Kundennummer {company.customerNumber} — wird beim Laden der Projekte automatisch zugeordnet
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8125rem', color: '#dc2626', marginTop: '0.5rem', fontWeight: 500 }}>
                    Keine Kundennummer hinterlegt
                  </div>
                )}
              </div>
            )}

            {/* Unternehmensdaten.org */}
            <div className={styles.card}>
              <div className={styles.cardTitleRow}>
                <h2 className={styles.cardTitle} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 0 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 22, height: 22, borderRadius: 'var(--radius-sm)',
                    background: '#ede9fe', color: '#7c3aed',
                  }}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width={14} height={14}>
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                    </svg>
                  </span>
                  Unternehmensdaten.org
                </h2>
                <button
                  onClick={() => {
                    enrichCompany.mutate(company.id, {
                      onSuccess: (res) => {
                        const suggestions = Object.entries(res.data.suggestions).map(
                          ([field, s]) => ({ field, ...s }),
                        );
                        setEnrichSuggestions(suggestions);
                        setEnrichOpen(true);
                      },
                    });
                  }}
                  disabled={enrichCompany.isPending}
                  className={styles.lexwareActionBtnPrimary}
                  style={{ borderColor: '#a5b4fc', color: '#4f46e5' }}
                >
                  {enrichCompany.isPending ? 'Anreichern...' : 'Jetzt anreichern'}
                </button>
              </div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.5rem 0 0' }}>
                Datenanreicherung aus öffentlichen Handelsregister- und Firmendaten.
              </p>
              {company.dataEnrichedAt ? (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                  <span style={{ color: '#059669', fontWeight: 500 }}>Angereichert</span>
                  {' '}am {formatDate(company.dataEnrichedAt)}
                  {company.dataEnrichedSource && (
                    <span> · Quelle: {company.dataEnrichedSource}</span>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                  Noch nicht angereichert
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ======== Modals ======== */}
      <CompanyFormModal
        isOpen={isEditOpen}
        onClose={() => setEditOpen(false)}
        company={company}
        onSuccess={() => setEditOpen(false)}
      />

      {/* Modal: Neuen Kontakt anlegen (dem Unternehmen zugeordnet) */}
      <ContactFormModal
        isOpen={isCreateContactOpen}
        onClose={() => setCreateContactOpen(false)}
        onSuccess={() => setCreateContactOpen(false)}
        preselectedCompany={{ id: company.id, name: company.name }}
      />

      <LexwareSearchModal
        isOpen={isLexwareSearchOpen}
        onClose={() => setLexwareSearchOpen(false)}
        onLink={(lexwareId) => {
          linkCompany.mutate(
            { lexwareContactId: lexwareId, companyId: company.id },
            { onSuccess: () => setLexwareSearchOpen(false) },
          );
        }}
        isLinking={linkCompany.isPending}
      />

      {/* ClockodoSearchModal entfernt — Auto-Match via Kundennummer */}

      {/* Enrichment Suggestions Modal */}
      <Modal
        isOpen={isEnrichOpen}
        onClose={() => setEnrichOpen(false)}
        title="Datenanreicherung — Vorschläge"
        maxWidth="600px"
      >
        {enrichSuggestions.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            Keine neuen Vorschläge vorhanden.
          </p>
        ) : (
          <div>
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
              Folgende Daten wurden gefunden. Klicke auf &quot;Übernehmen&quot;, um einzelne Vorschläge zu übernehmen.
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>Feld</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>Aktuell</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>Vorschlag</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>Quelle</th>
                  <th style={{ padding: '0.5rem', width: '80px' }}></th>
                </tr>
              </thead>
              <tbody>
                {enrichSuggestions.map((s, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.5rem', fontWeight: 500 }}>{s.field}</td>
                    <td style={{ padding: '0.5rem', color: 'var(--color-text-muted)' }}>
                      {s.current ?? '—'}
                    </td>
                    <td style={{ padding: '0.5rem', color: '#4f46e5', fontWeight: 500 }}>
                      {s.suggested ?? '—'}
                    </td>
                    <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      {s.source}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      {s.suggested && (
                        <button
                          onClick={() => {
                            updateCompany.mutate(
                              { id: company.id, data: { [s.field]: s.suggested } },
                              {
                                onSuccess: () => {
                                  setEnrichSuggestions((prev) =>
                                    prev.filter((_, idx) => idx !== i),
                                  );
                                },
                              },
                            );
                          }}
                          disabled={updateCompany.isPending}
                          style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            background: '#4f46e5',
                            color: 'white',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Übernehmen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button
            onClick={() => setEnrichOpen(false)}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            Schließen
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Unternehmen löschen"
        maxWidth="420px"
      >
        <p
          style={{
            fontSize: '0.9375rem',
            color: 'var(--color-text)',
            marginBottom: '0.5rem',
          }}
        >
          Soll das Unternehmen <strong>{company.name}</strong> wirklich gelöscht
          werden?
        </p>
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--color-error)',
            marginBottom: '1.5rem',
          }}
        >
          Verknüpfte Kontakte und Vorgänge verlieren die Zuordnung.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
          }}
        >
          <button
            onClick={() => setDeleteOpen(false)}
            disabled={deleteMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={() =>
              deleteMutation.mutate(company.id, {
                onSuccess: () => navigate('/crm?tab=companies'),
              })
            }
            disabled={deleteMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--color-error)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: deleteMutation.isPending ? 'wait' : 'pointer',
              opacity: deleteMutation.isPending ? 0.7 : 1,
            }}
          >
            {deleteMutation.isPending ? 'Löschen...' : 'Endgültig löschen'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

